import { useState, useEffect, useCallback } from 'react'
import { TransactionList } from '@/components/dashboard/TransactionList'
import { QuickAddForm } from '@/components/forms/QuickAddForm'
import { DonutChart } from '@/components/charts/DonutChart'
import { BarChart } from '@/components/charts/BarChart'
import { formatCurrency } from '@/components/charts/chartUtils'

interface CategoryData {
  id: number
  name: string
  icon: string | null
  color: string
  amount: number
  percentage: number
  monthly_limit?: number
}

interface SummaryData {
  expenses: {
    total: number
    byCategory: CategoryData[]
  }
}

export function Expenses() {
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [summary, setSummary] = useState<SummaryData | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchSummary = useCallback(async () => {
    try {
      const response = await fetch('/api/summary?period=month')
      if (response.ok) {
        const data = (await response.json()) as SummaryData
        setSummary(data)
      }
    } catch (error) {
      console.error('Error fetching summary:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary, refreshTrigger])

  function handleAddSuccess() {
    setRefreshTrigger((prev) => prev + 1)
  }

  // Transform category data for donut chart
  const donutData =
    summary?.expenses.byCategory.map((cat) => ({
      name: `${cat.icon || ''} ${cat.name}`.trim(),
      value: cat.amount,
      color: cat.color
    })) || []

  // Transform data for bar chart (spending vs limit)
  const barData =
    summary?.expenses.byCategory
      .filter((cat) => cat.monthly_limit && cat.monthly_limit > 0)
      .map((cat) => ({
        name: cat.name,
        value: cat.amount,
        limit: cat.monthly_limit,
        color: cat.color
      })) || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-forest-dark">Expenses</h1>
          <p className="text-sage mt-1">Track and manage your spending</p>
        </div>
        <button
          onClick={() => setIsQuickAddOpen(true)}
          className="px-4 py-2 bg-forest text-white rounded-lg text-sm hover:bg-forest-dark transition-colors"
        >
          + Add Expense
        </button>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Breakdown Donut Chart */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
          <h2 className="text-lg font-semibold text-charcoal mb-4">
            Category Breakdown
          </h2>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-sage">Loading...</div>
            </div>
          ) : donutData.length > 0 ? (
            <DonutChart
              data={donutData}
              centerValue={formatCurrency(summary?.expenses.total || 0)}
              centerLabel="Total Spent"
              height={280}
            />
          ) : (
            <div className="text-center py-12">
              <p className="text-sage">No expenses this month</p>
              <p className="text-sm text-stone mt-2">
                Add transactions to see your spending breakdown
              </p>
            </div>
          )}
        </div>

        {/* Spending vs Budget Bar Chart */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
          <h2 className="text-lg font-semibold text-charcoal mb-4">
            Spending vs Budget
          </h2>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-sage">Loading...</div>
            </div>
          ) : barData.length > 0 ? (
            <BarChart
              data={barData}
              layout="vertical"
              colorByValue={true}
              showLimits={true}
              label1="Spent"
              height={280}
            />
          ) : (
            <div className="text-center py-12">
              <p className="text-sage">No budget limits set</p>
              <p className="text-sm text-stone mt-2">
                Set category limits to track spending against your budget
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
        <h2 className="text-lg font-semibold text-charcoal mb-4">
          Recent Expenses
        </h2>
        <TransactionList
          type="expense"
          limit={20}
          showFilters={true}
          refreshTrigger={refreshTrigger}
        />
      </div>

      {/* Quick Add Modal */}
      <QuickAddForm
        isOpen={isQuickAddOpen}
        onClose={() => setIsQuickAddOpen(false)}
        onSuccess={handleAddSuccess}
        defaultType="expense"
      />
    </div>
  )
}
